import axios from 'axios';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  IAcceptVTOActionPayload,
  IDeleteShiftRequest,
  IGetClientShiftDetails,
  IGetClientShifts,
  IGetSchedulesRequest,
  IOfferShiftRequest,
  IUpcomingShiftRaw,
  IUpdateShiftRequest,
  IUpsertShiftByDay,
  IUpsertShiftByWeek,
} from '@/api/types';
import type {
  IAssignAvailableShiftsAction,
  IAssignAvailableShiftsActionReturn,
  ISchedulesState,
  IThunkAPI,
} from '@/store/types';
import type { ISchedule, IScheduleBlockAvailable, ISortedAvailableShift, ScheduleId } from '@/types/schedule';
import type { IHeadcountShift, IHeadcountShiftDetail } from '@/components/client/scheduling/types';

import { schedule as api } from '@/api';
import { ShiftAssignStatus } from '@/store/enums';
import {
  getActionPrefixes,
  isActionFromSlice,
  isFulfilledAction,
  isKnownError,
  isPendingAction,
  isRejectedAction,
  logError,
  makeCancellable,
  mapScheduleColors,
  onFulfilledAction,
  onPendingAction,
  onRejectedAction,
} from '@/helpers';
import { removeDuplicatedShifts, transformHeadcountShifts } from '@/api/transforms';
import { SLICES } from '@/constants/actions';

const initialState: ISchedulesState = {
  availableShifts: [],
  upcomingShifts: [],
  scheduleColors: null,
  schedulesList: null,
  selectedSchedulesIds: [],
  selectedAvailableShifts: [],
  scheduleIdsToShowDetailsFor: [],
  //SU
  client: {
    schedules: [],
    shifts: [],
    shiftDetails: null,
    selectedSchedule: null,
  },
  //META
  meta: {
    pending: [],
  },
};

const { suPrefix, spPrefix } = getActionPrefixes(SLICES.SCHEDULE);
/**
 * SP
 */

export const getShifts = createAsyncThunk<{ scheduleList: ISchedule[] }, IGetSchedulesRequest, IThunkAPI>(
  `${spPrefix}/shifts/assigned`,
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getShifts cancelled');
      });
      const response = await api.getShifts(payload, { cancelToken: source.token });

      return { scheduleList: removeDuplicatedShifts(response.data.scheduleList) };
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

export const getAvailableShifts = createAsyncThunk<IScheduleBlockAvailable[], IGetSchedulesRequest, IThunkAPI>(
  `${spPrefix}/shifts/available`,
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getAvailableShifts cancelled');
      });
      const response = await api.getAvailableShifts(payload, { cancelToken: source.token });
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

export const getUpcomingShifts = createAsyncThunk<IUpcomingShiftRaw[], undefined, IThunkAPI>(
  `${spPrefix}/shifts/upcoming`,
  async (_, thunkAPI) => {
    try {
      const getUpcomingShiftsRequest = makeCancellable(api.getUpcomingShifts, thunkAPI.signal);
      const response = await getUpcomingShiftsRequest();
      if (isKnownError(response.data)) throw response.data;

      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

export const assignAvailableShifts = createAsyncThunk<
  IAssignAvailableShiftsActionReturn,
  IAssignAvailableShiftsAction,
  IThunkAPI
>(`${spPrefix}/shifts/available/assign`, async ({ shiftsIds, weekBlocks }, thunkAPI) => {
  try {
    const source = axios.CancelToken.source();
    thunkAPI.signal.addEventListener('abort', () => {
      source.cancel('assignAvailableShifts cancelled');
    });

    const assignResponse = await api.assignAvailableShifts(shiftsIds, { cancelToken: source.token });
    if (weekBlocks) {
      const [shiftsResponse, availableShiftsResponse] = await Promise.all([
        api.getShifts(weekBlocks, { cancelToken: source.token }),
        api.getAvailableShifts(weekBlocks, { cancelToken: source.token }),
      ]);

      return {
        unpickedShifts: assignResponse.data,
        shifts: shiftsResponse.data.scheduleList,
        availableShifts: availableShiftsResponse.data,
      };
    }

    return {
      unpickedShifts: assignResponse.data,
      shifts: null,
      availableShifts: null,
    };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message);
  }
});

export const offerShift = createAsyncThunk<boolean, IOfferShiftRequest, IThunkAPI>(
  `${spPrefix}/shifts/offer`,
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('offerShift cancelled');
      });
      const response = await api.offerShift(payload, { cancelToken: source.token });
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

export const acceptVTO = createAsyncThunk<boolean, IAcceptVTOActionPayload, IThunkAPI>(
  `${spPrefix}/shifts/vto`,
  async (payload, thunkAPI) => {
    try {
      const response = await api.acceptVTO(payload.shiftAssnId);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

/**
 * SU
 */

export const getClientSchedules = createAsyncThunk<ISchedule[], { clientId: number }, IThunkAPI>(
  `${suPrefix}/list`,
  async (payload, thunkAPI) => {
    try {
      const getClientSchedules = makeCancellable(api.getClientSchedules, thunkAPI.signal);
      const { data } = await getClientSchedules(payload);
      if (isKnownError(data)) return [];
      return data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getClientShifts = createAsyncThunk<IHeadcountShift[][], IGetClientShifts, IThunkAPI>(
  `${suPrefix}/shifts`,
  async (payload, thunkAPI) => {
    try {
      const getClientShifts = makeCancellable(api.getClientShifts, thunkAPI.signal);
      const response = await getClientShifts(payload);
      if (typeof response.data === 'string') return [];
      return transformHeadcountShifts(response.data, payload);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getClientShiftDetails = createAsyncThunk<IHeadcountShiftDetail, IGetClientShiftDetails, IThunkAPI>(
  `${suPrefix}/shift/details`,
  async (payload, thunkAPI) => {
    try {
      const getClientShiftDetails = makeCancellable(api.getClientShiftDetails, thunkAPI.signal);
      const { data } = await getClientShiftDetails({
        scheduleId: payload.scheduleId,
        shiftId: payload.shiftId,
      });
      if (typeof data === 'string') throw data;
      return data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const upsertShiftByWeek = createAsyncThunk<IHeadcountShift[][], IUpsertShiftByWeek, IThunkAPI>(
  `${suPrefix}/upsertbyweek`,
  async (payload, thunkAPI) => {
    try {
      const upsertShiftByWeek = makeCancellable(api.upsertShiftByWeek, thunkAPI.signal);

      const response = await upsertShiftByWeek(payload);
      if (typeof response.data === 'string') throw response.data;

      const getClientShifts = makeCancellable(api.getClientShifts, thunkAPI.signal);
      const responseShifts = await getClientShifts({ weekId: payload.weekId, scheduleId: payload.scheduleId });

      if (typeof responseShifts.data === 'string') throw responseShifts.data;
      return transformHeadcountShifts(responseShifts.data, payload);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const updateShift = createAsyncThunk<IHeadcountShift[][], IUpdateShiftRequest, IThunkAPI>(
  `${suPrefix}/shift/update`,
  async (payload, thunkAPI) => {
    try {
      const response = await makeCancellable(api.updateShift, thunkAPI.signal)(payload);
      if (typeof response.data === 'string') throw response.data;

      const getClientShifts = makeCancellable(api.getClientShifts, thunkAPI.signal);
      const responseShifts = await getClientShifts({ weekId: payload.weekId, scheduleId: payload.scheduleId });

      if (typeof responseShifts.data === 'string') throw responseShifts.data;
      return transformHeadcountShifts(responseShifts.data, payload);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const deleteShift = createAsyncThunk<IHeadcountShift[][] | undefined, IDeleteShiftRequest, IThunkAPI>(
  `${suPrefix}/shift/delete`,
  async (payload, thunkAPI) => {
    try {
      const response = await makeCancellable(api.deleteShift, thunkAPI.signal)(payload);
      if (typeof response.data === 'string') throw response.data;
      if (response.data !== true) return;

      const getClientShifts = makeCancellable(api.getClientShifts, thunkAPI.signal);
      const responseShifts = await getClientShifts({ weekId: payload.weekId, scheduleId: payload.scheduleId });

      if (typeof responseShifts.data === 'string') throw responseShifts.data;
      return transformHeadcountShifts(responseShifts.data, payload);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const upsertShiftByDay = createAsyncThunk<IHeadcountShift[][], IUpsertShiftByDay, IThunkAPI>(
  `${suPrefix}/upsertbyday`,
  async (payload, thunkAPI) => {
    try {
      const upsertShiftByDay = makeCancellable(api.upsertShiftByDay, thunkAPI.signal);

      const response = await upsertShiftByDay(payload);
      if (typeof response.data === 'string') throw response.data;

      const getClientShifts = makeCancellable(api.getClientShifts, thunkAPI.signal);
      const responseShifts = await getClientShifts({ weekId: payload.weekId, scheduleId: payload.scheduleId });

      if (typeof responseShifts.data === 'string') throw responseShifts.data;
      return transformHeadcountShifts(responseShifts.data, payload);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const slice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    clearSchedules(state) {
      state.scheduleColors = null;
      state.schedulesList = null;
      state.availableShifts = [];
      state.selectedSchedulesIds = [];
      state.scheduleIdsToShowDetailsFor = [];
      state.selectedAvailableShifts = [];
    },
    clearAvailableShifts(state) {
      state.availableShifts = [];
    },
    clearSelectedAvailableShifts(state) {
      state.selectedAvailableShifts = [];
    },
    clearState(state) {
      state.meta.pending = [];
    },
    toggleDisplayDetailsOnGrouppedSchedule(
      state,
      action: PayloadAction<{
        id: ScheduleId;
        shouldDisplay: boolean;
      }>,
    ) {
      const { id, shouldDisplay } = action.payload;

      if (shouldDisplay) {
        if (!state.scheduleIdsToShowDetailsFor.includes(id)) state.scheduleIdsToShowDetailsFor.push(id);
      } else {
        const indexOfId = state.scheduleIdsToShowDetailsFor.indexOf(id);
        if (indexOfId >= 0) state.scheduleIdsToShowDetailsFor.splice(indexOfId, 1);
      }
    },
    setSelectedSchedulesIds(state, action) {
      state.selectedSchedulesIds = action.payload;
    },
    toggleIsSelectedAvailableShifts(state, action: PayloadAction<ISortedAvailableShift>) {
      const item = state.selectedAvailableShifts.find(item => item.shiftId === action.payload.shiftId);

      if (item) {
        state.selectedAvailableShifts = state.selectedAvailableShifts.filter(
          shift => shift.shiftId !== action.payload.shiftId,
        );
      } else {
        const availableShiftToAdd = { ...action.payload };
        if (action.payload.splitStart || action.payload.splitEnd) {
          const originalShift = state.availableShifts.find(shift => shift.shiftId === action.payload.shiftId);
          if (!originalShift) return;
          availableShiftToAdd.startBlock = originalShift.startBlock;
          availableShiftToAdd.endBlock = originalShift.endBlock;
        }
        state.selectedAvailableShifts.push(availableShiftToAdd);
      }
      state.selectedAvailableShifts.sort((a, b) => a.startBlock - b.startBlock);
    },
    setSelectedClientSchedule(state, action: PayloadAction<{ schedule: ISchedule }>) {
      state.client.selectedSchedule = action.payload.schedule;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getClientShiftDetails.fulfilled, (state, action) => {
        state.client.shiftDetails = action.payload;
      })
      .addCase(getClientSchedules.fulfilled, (state, action) => {
        state.client.schedules = action.payload;
        if (action.payload.length) {
          state.client.selectedSchedule = action.payload?.filter(schedule => schedule.isActive)?.[0];
        }
      })
      .addCase(getClientShifts.fulfilled, (state, action) => {
        state.client.shifts = action.payload;
      })
      .addCase(upsertShiftByWeek.fulfilled, (state, action) => {
        state.client.shifts = action.payload;
      })
      .addCase(upsertShiftByDay.fulfilled, (state, action) => {
        state.client.shifts = action.payload;
      })
      .addCase(updateShift.fulfilled, (state, action) => {
        state.client.shifts = action.payload;
      })
      .addCase(deleteShift.fulfilled, (state, action) => {
        if (action.payload) state.client.shifts = action.payload;
      })
      .addCase(getShifts.fulfilled, (state, action) => {
        const list = action.payload.scheduleList.sort((a, b) => a.scheduleId - b.scheduleId);
        state.scheduleColors = mapScheduleColors(list);
        state.schedulesList = list;
      })
      .addCase(getAvailableShifts.fulfilled, (state, action) => {
        state.availableShifts = action.payload;
      })
      .addCase(getUpcomingShifts.fulfilled, (state, action) => {
        state.upcomingShifts = action.payload;
      })
      .addCase(offerShift.fulfilled, (state, action) => {
        const { onOffer, scheduleId, shiftAssnId } = action.meta.arg;
        const schedule = state.schedulesList?.find(schedule => schedule.scheduleId === scheduleId);
        const shift = schedule?.shifts.find(shift => shift.shiftAssnId === shiftAssnId);
        if (shift) {
          shift.isOffered = onOffer;
          shift.shiftAssnStatus = onOffer ? ShiftAssignStatus.offered : ShiftAssignStatus.default;
        }
      })
      .addCase(acceptVTO.fulfilled, (state, action) => {
        const { scheduleId, shiftAssnId } = action.meta.arg;
        const schedule = state.schedulesList?.find(schedule => schedule.scheduleId === scheduleId);
        const shift = schedule?.shifts.find(shift => shift.shiftAssnId === shiftAssnId);
        if (shift) {
          shift.shiftAssnStatus = ShiftAssignStatus.vtoAccepted;
        }
      })
      .addCase(assignAvailableShifts.fulfilled, (state, action) => {
        if (action.payload.shifts) {
          const list = removeDuplicatedShifts(action.payload.shifts).sort((a, b) => a.scheduleId - b.scheduleId);
          state.scheduleColors = mapScheduleColors(list);
          state.schedulesList = list;
        }

        if (action.payload.availableShifts) {
          state.availableShifts = action.payload.availableShifts;
        }

        state.selectedAvailableShifts = [];
      })
      .addMatcher(action => isFulfilledAction(action) && isActionFromSlice(action, SLICES.SCHEDULE), onFulfilledAction)
      .addMatcher(action => isRejectedAction(action) && isActionFromSlice(action, SLICES.SCHEDULE), onRejectedAction)
      .addMatcher(action => isPendingAction(action) && isActionFromSlice(action, SLICES.SCHEDULE), onPendingAction);
  },
});

// actions
export const {
  clearAvailableShifts,
  clearState,
  clearSchedules,
  clearSelectedAvailableShifts,
  setSelectedSchedulesIds,
  toggleIsSelectedAvailableShifts,
  toggleDisplayDetailsOnGrouppedSchedule,
  setSelectedClientSchedule,
} = slice.actions;

export default slice.reducer;
